/* eslint-disable complexity */
/* eslint-disable class-methods-use-this */
/* eslint-disable camelcase */
import React from 'react';
import PropTypes from 'prop-types';
import BaseClass from 'components/BaseClass';
import { wrapComponent } from 'utils/framework';
import {
    Box, Button, Link, Text, Grid, Container
} from 'components/ui';
import ProductTile from 'components/Catalog/ProductGrid/ProductTile';
import Chiclet from 'components/Chiclet';
import Action from 'components/Content/Action';
import getStyles from 'components/Catalog/ProductGrid/styles';
import SkeletonBanner from 'components/Banner/SkeletonBanner/SkeletonBanner';
import mediaUtils from 'utils/Media';
import { breakpoints, space, mediaQueries } from 'style/config';
import MarketingTile from 'components/Catalog/ProductGrid/MarketingTile';
import Breadcrumb from 'components/Content/Breadcrumb';
import contentStoreLayoutProductGridBindings from 'analytics/bindingMethods/pages/contentStore/contentStoreLayoutProductGridBindings';
import CatalogPageBindings from 'analytics/bindingMethods/pages/catalog/catalogPageBindings';
import { RESULTS_COUNT, ALL, CONSTRUCTOR_PODS } from 'constants/constructorConstants';
import { ML_VS_CONSTRUCTOR } from 'constants/TestTarget';
import Empty from 'constants/empty';
import helpersUtils from 'utils/Helpers';
import catalogUtils from 'utils/Catalog';

const { deferTaskExecution } = helpersUtils;

const NUMBER_OF_TILES = RESULTS_COUNT.CONTENT_PAGE_SIZE;
const ActionLink = Action(Link);
const ActionChiclet = Action(Chiclet);
const { Media } = mediaUtils;
const { setPageLoadAnalytics, getNavigationInfo, getRootContainerName } = contentStoreLayoutProductGridBindings;

import RegularFilters from 'components/Catalog/Filters';
import RegularFilterChiclets from 'components/Catalog/Filters/FilterChiclets';
import { withUpperFunnelProps } from 'viewModel/catalog/upperFunnel/withUpperFunnelProps';
const Filters = withUpperFunnelProps(RegularFilters);
const FilterChiclets = withUpperFunnelProps(RegularFilterChiclets);

class LayoutProductGrid extends BaseClass {
    constructor(props) {
        super(props);
        this.navRef = React.createRef();
        this.state = {
            pageNum: 1,
            selectedFilters: {},
            appliedFilters: {}
        };
        this.unsubscribeStore = Empty.Function;
        this.unsubscribeZipCode = Empty.Function;
        this.fetched = false;
    }

    fetchInitialLegacyPageData = () => {
        if (this.props.layoutExperience === ML_VS_CONSTRUCTOR.ML && this.props.user.isInitialized) {
            this.props.getBestSellersRecs({
                userId: this.props.user.userId,
                catId: this.props.content.certonaCategoryId
            });
        }

        if (this.props.layoutExperience === ML_VS_CONSTRUCTOR.CONSTRUCTOR) {
            this.updateConstructorRecommendations();
        }

        this.fetched = true;
    };

    fetchInitialSasPageData = () => {
        const {
            podId,
            isBestsellersConstructorPage,
            isNewConstructorPage,
            onStoreChangedFromHeader,
            onZipCodeChangedFromHeader,
            fetchBestsellersProducts,
            fetchNewProducts
        } = this.props;

        if (isBestsellersConstructorPage) {
            fetchBestsellersProducts(podId);
        } else if (isNewConstructorPage) {
            fetchNewProducts(podId);
        }

        this.unsubscribeStore = onStoreChangedFromHeader();
        this.unsubscribeZipCode = onZipCodeChangedFromHeader();
        this.fetched = true;
    };

    applySelectedFilters = () => {
        const { selectedFilters, refinements } = this.state;
        const { applyFilters } = this.props;
        const filtersToApply = catalogUtils.createFiltersToApply(selectedFilters, refinements);

        applyFilters(filtersToApply);
    };

    applySelectionIfChanged = (newSelectedFilters, applyFilters = false) => {
        const { selectedFilters: currentSelectedFilters } = this.state;

        if (JSON.stringify(newSelectedFilters) !== JSON.stringify(currentSelectedFilters)) {
            this.setState(
                {
                    selectedFilters: newSelectedFilters
                },
                () => {
                    if (applyFilters) {
                        this.applySelectedFilters();
                    }
                }
            );
        }
    };

    selectFilters = (filtersToSelect, applyFilters = false) => {
        const newSelectedFilters = catalogUtils.addToSelection(this.state.selectedFilters, filtersToSelect);
        this.applySelectionIfChanged(newSelectedFilters, applyFilters);
    };

    clearFiltersSelection = (applyFilters, resetSortToDefault) => {
        const { selectedFilters } = this.state;
        const newSelectedFilters = catalogUtils.resetSelection(selectedFilters, resetSortToDefault);
        this.applySelectionIfChanged(newSelectedFilters, applyFilters);
    };

    removeFilterValue = (filterKey, filterValue, applyFilters = false) => {
        const { selectedFilters } = this.state;
        const newSelectedFilters = catalogUtils.removeValueFromSelection(selectedFilters, filterKey, filterValue);
        this.applySelectionIfChanged(newSelectedFilters, applyFilters);
    };

    loadMoreProducts = () => {
        deferTaskExecution(() => {
            const {
                podId, shouldFetchDataFromSAS, isBestsellersConstructorPage, isNewConstructorPage, fetchBestsellersProducts, fetchNewProducts
            } =
                this.props;
            const { pageNum } = this.state;

            if (shouldFetchDataFromSAS) {
                const currentPage = pageNum + 1;

                if (isBestsellersConstructorPage) {
                    fetchBestsellersProducts(podId, currentPage, undefined, true);
                } else if (isNewConstructorPage) {
                    fetchNewProducts(podId, currentPage, undefined, true);
                }

                this.setState({ pageNum: currentPage });

                return;
            }

            const nextPage = this.state.pageNum + 1;

            this.setState({
                pageNum: nextPage
            });
        });
    };

    updateConstructorRecommendations = () => {
        const { content, podId, isNewContentPage, isCollection } = this.props;
        this.setState({ pageNum: 1 }, () => {
            const params = {
                ...(isNewContentPage ? { resultsPerPage: RESULTS_COUNT.NEW_CONTENT_PAGE } : { numResults: RESULTS_COUNT.BESTSELLERS }),
                ...(podId !== CONSTRUCTOR_PODS.NEW_CONTENT_PAGE_JUST_DROPPED && {
                    filters: {
                        group_id: content.certonaCategoryId || ALL
                    }
                })
            };
            this.props.updateRequestData({ params, podId, isCollection });
        });
    };

    componentDidMount() {
        const { shouldFetchDataFromSAS } = this.props;

        if (shouldFetchDataFromSAS) {
            this.fetchInitialSasPageData();
        } else {
            this.fetchInitialLegacyPageData();
        }

        setPageLoadAnalytics(this.props.breadcrumbs);

        const activeIndex = this.props.navigation?.items.findIndex(link => link.action?.isCurrent);

        if (activeIndex !== -1 && window.matchMedia(breakpoints.smMax).matches) {
            const activeLink = this.navRef.current?.children[activeIndex];

            if (activeLink?.offsetWidth + activeLink?.offsetLeft > window.innerWidth) {
                activeLink.scrollIntoView({ inline: 'center' });
            }
        }
    }

    componentWillUnmount() {
        this.unsubscribeStore();
        this.unsubscribeZipCode();
    }

    componentDidUpdate(prevProps) {
        if (
            this.props.layoutExperience === ML_VS_CONSTRUCTOR.ML &&
            (prevProps.user.isInitialized !== this.props.user.isInitialized || prevProps.layoutExperience !== this.props.layoutExperience)
        ) {
            this.props.getBestSellersRecs({
                userId: this.props.user.userId,
                catId: this.props.content.certonaCategoryId
            });
        }

        if (this.props.layoutExperience === ML_VS_CONSTRUCTOR.CONSTRUCTOR && this.props?.podId !== prevProps?.podId) {
            this.updateConstructorRecommendations();
        }

        // For page updates initiated from Left Nav / Breadcrumbs
        if (prevProps.content.sid !== this.props.content.sid) {
            if (this.props.layoutExperience === ML_VS_CONSTRUCTOR.ML) {
                this.props.user.isInitialized &&
                    this.setState({ pageNum: 1 }, () => {
                        this.props.getBestSellersRecs({
                            userId: this.props.user.userId,
                            catId: this.props.content.certonaCategoryId
                        });
                    });
            } else {
                this.setState({ pageNum: 1 });
            }

            setPageLoadAnalytics(this.props.breadcrumbs);
        }
    }

    static getDerivedStateFromProps(props, state) {
        const { contextId, refinements, podId, shopYourStoreNewBestEnabled } = props;
        const { prevContextId, selectedFilters, prevPageId } = state;

        if (contextId === prevContextId) {
            return null;
        }

        const pageId = podId;
        const isSamePage = pageId === prevPageId;
        const result = catalogUtils.createFiltersWithSortRefinements({ refinements }, selectedFilters, isSamePage, false, false);
        const fireAnalytics = shopYourStoreNewBestEnabled && prevContextId !== null;

        if (fireAnalytics) {
            if (isSamePage) {
                CatalogPageBindings.fireAsyncPageLoadAnalytics({ refinements }, result.categoryFilters); // on filters change
            }
        }

        return {
            prevPageId: pageId,
            prevContextId: contextId,
            selectedFilters: result.filters,
            appliedFilters: result.filters,
            refinements: result.withSortRefinements
        };
    }

    render() {
        const {
            localization,
            content,
            seo,
            navigation,
            breadcrumbs,
            isLoading = true,
            podId,
            contextId,
            isSasInitialized,
            shopYourStoreNewBestEnabled,
            shouldFetchDataFromSAS,
            showLoadMoreProductsBtn,
            zeroResults,
            recs,
            totalResults,
            resultId,
            isCollection,
            deliveryOptions
        } = this.props;
        const { refinements, selectedFilters, appliedFilters } = this.state;

        const { marketingTiles, marketingTilePositions, certonaCategoryId } = content;
        const gridLength = NUMBER_OF_TILES * this.state.pageNum;
        const skus = shouldFetchDataFromSAS ? recs : recs?.slice(0, gridLength);
        const showLoadMoreproducts = shouldFetchDataFromSAS ? showLoadMoreProductsBtn : recs?.length > gridLength;
        const checkedRefinements = catalogUtils.getSelectedRefinements(refinements).checkedIds;
        const similarProducts = Sephora.isDesktop() && !!certonaCategoryId;
        const tileStyles = getStyles();

        return (
            <Container>
                {breadcrumbs && (
                    <Breadcrumb
                        key={contextId}
                        persistRefinements={true}
                        breadcrumbs={breadcrumbs}
                        localization={localization?.breadcrumb}
                    />
                )}
                <Grid
                    gap={[3, null, 7]}
                    columns={[null, null, '236px 1fr']}
                    marginTop={!breadcrumbs && [4, 6]}
                >
                    <Box lineHeight='tight'>
                        {seo?.header1 && (
                            <Text
                                is='p'
                                fontSize={['lg', 'xl']}
                                marginBottom={[3, 5]}
                                fontWeight='bold'
                            >
                                {seo.header1}
                            </Text>
                        )}
                        {navigation && (
                            <ul
                                key={contextId}
                                css={styles.nav}
                                ref={this.navRef}
                            >
                                {(navigation.items || []).map(link => (
                                    <li key={`navLink_${link.label}`}>
                                        <Media lessThan='md'>
                                            <ActionChiclet
                                                persistRefinements={true}
                                                variant='shadow'
                                                sid={link.sid}
                                                isActive={link.action?.isCurrent}
                                                action={link.action}
                                                children={link.label}
                                                analyticsNextPageData={getNavigationInfo(breadcrumbs, link.label)}
                                            />
                                        </Media>
                                        <Media greaterThan='sm'>
                                            <ActionLink
                                                persistRefinements={true}
                                                sid={link.sid}
                                                display='block'
                                                paddingY='.5em'
                                                fontWeight={link.action?.isCurrent && 'bold'}
                                                action={link.action}
                                                children={link.label}
                                                analyticsNextPageData={getNavigationInfo(breadcrumbs, link.label)}
                                            />
                                        </Media>
                                    </li>
                                ))}
                            </ul>
                        )}
                        {shopYourStoreNewBestEnabled && (
                            <Box marginTop={[3, 3, isSasInitialized ? 0 : 6]}>
                                {isLoading ? (
                                    <SkeletonBanner height={[40, null, 250]} />
                                ) : (
                                    <Filters
                                        contextId={contextId}
                                        deliveryOptions={deliveryOptions}
                                        selectedFilters={selectedFilters}
                                        appliedFilters={appliedFilters}
                                        refinements={refinements}
                                        selectFilters={this.selectFilters}
                                        removeFilterValue={this.removeFilterValue}
                                        forceCompactPills={true}
                                        showMegaFilterPill={false}
                                    />
                                )}
                            </Box>
                        )}
                    </Box>
                    <main>
                        <FilterChiclets
                            entry='filter chiclet'
                            forceUpperFunnelChiclets
                            refinements={refinements}
                            filters={appliedFilters}
                            deliveryOptions={deliveryOptions}
                            selectedFilters={selectedFilters}
                            selectFilters={this.selectFilters}
                            onRemoveChiclet={this.removeFilterValue}
                            onClearAllFilters={this.clearFiltersSelection}
                        />
                        <div
                            css={[tileStyles.root, zeroResults && tileStyles.zeroResults]}
                            {...(podId &&
                                !isCollection && {
                                'data-cnstrc-recommendations': true,
                                'data-cnstrc-recommendations-pod-id': podId,
                                'data-cnstrc-result-id': resultId,
                                'data-cnstrc-num-results': totalResults
                            })}
                            {...(podId &&
                                isCollection && {
                                'data-cnstrc-browse': true,
                                ...(totalResults === 0 ? { 'data-cnstrc-zero-result': true } : {}),
                                'data-cnstrc-result-id': resultId,
                                'data-cnstrc-num-results': totalResults,
                                'data-cnstrc-filter-name': 'collection_id',
                                'data-cnstrc-filter-value': podId
                            })}
                        >
                            {(marketingTiles || []).map((marketingTile, tileIndex) => {
                                const order = marketingTilePositions[tileIndex] - 1;

                                return (
                                    <div
                                        key={marketingTile.sid || marketingTile.imageSource}
                                        css={tileStyles.item}
                                        style={{ alignSelf: 'flex-start', order }}
                                    >
                                        <MarketingTile content={marketingTile} />
                                    </div>
                                );
                            })}
                            {zeroResults && (
                                <Box
                                    lineHeight='tight'
                                    marginY={5}
                                >
                                    <Text
                                        is='h2'
                                        fontWeight='bold'
                                        fontSize={['md', 'lg']}
                                        marginBottom='.25em'
                                        data-at={Sephora.debug.dataAt('filter_error')}
                                        children={localization.noResults1}
                                    />
                                    <Text
                                        is='p'
                                        fontSize={[null, 'md']}
                                        whiteSpace='normal'
                                        children={localization.noResults2}
                                    />
                                </Box>
                            )}
                            {isLoading
                                ? [...Array(NUMBER_OF_TILES).keys()].map((item, index) => (
                                    <div
                                        key={`productTileSkeleton_${index}`}
                                        css={tileStyles.item}
                                        style={{ order: index }}
                                    >
                                        <ProductTile isSkeleton={true} />
                                    </div>
                                ))
                                : (skus || []).map((sku, index) => (
                                    <div
                                        key={`${sku?.productId}_${sku?.skuId}`}
                                        css={tileStyles.item}
                                        style={{ order: index }}
                                    >
                                        <ProductTile
                                            product={sku}
                                            index={index}
                                            rootContainerName={getRootContainerName(breadcrumbs)}
                                            similarProducts={similarProducts}
                                            deliveryOptions={deliveryOptions}
                                            checkedRefinements={checkedRefinements}
                                        />
                                    </div>
                                ))}
                            {!isLoading && !zeroResults && (
                                <div css={[tileStyles.item, tileStyles.showMore]}>
                                    {skus?.length ? (
                                        <Text
                                            is='p'
                                            color='gray'
                                            lineHeight='tight'
                                            fontSize={['sm', 'base']}
                                            marginBottom={2}
                                            children={`${localization.viewing} 1-${skus.length} ${localization.results}`}
                                        />
                                    ) : null}
                                    {showLoadMoreproducts ? (
                                        <Button
                                            variant='secondary'
                                            css={tileStyles.showMoreButton}
                                            children={localization.showMore}
                                            onClick={this.loadMoreProducts}
                                        />
                                    ) : null}
                                </div>
                            )}
                        </div>
                    </main>
                </Grid>
            </Container>
        );
    }
}

const styles = {
    nav: {
        [mediaQueries.smMax]: {
            margin: `-${space[2]}px -${space.container}px`,
            padding: `${space[2]}px ${space.container}px`,
            fontSize: 0,
            whiteSpace: 'nowrap',
            overflowX: 'auto',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
            li: {
                display: 'inline-block',
                '+ li': {
                    marginLeft: space[2]
                }
            }
        },
        [mediaQueries.md]: {
            marginTop: '-.5em'
        }
    }
};

LayoutProductGrid.propTypes = {
    user: PropTypes.object.isRequired,
    localization: PropTypes.object.isRequired,
    layoutExperience: PropTypes.string,
    recs: PropTypes.array,
    isLoading: PropTypes.bool.isRequired,
    bestSellersMLFailed: PropTypes.bool.isRequired,
    content: PropTypes.object.isRequired,
    navigation: PropTypes.object,
    seo: PropTypes.object,
    breadcrumbs: PropTypes.array,
    totalResults: PropTypes.number,
    resultId: PropTypes.string,
    onStoreChangedFromHeader: PropTypes.func,
    onZipCodeChangedFromHeader: PropTypes.func
};

LayoutProductGrid.defaultProps = {
    navigation: null,
    seo: null,
    breadcrumbs: null,
    totalResults: null,
    resultId: null,
    onStoreChangedFromHeader: Empty.Function,
    onZipCodeChangedFromHeader: Empty.Function
};

export default wrapComponent(LayoutProductGrid, 'LayoutProductGrid', true);
